import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { FacadeService } from 'src/app/shared/services/facade.service';
import { EnboxConfigurationState } from '../reducers/enbox-configuration.reducer';
import * as selectors from '../selectors/enbox-configuration.selectors';

@Injectable()
export class EnboxConfigurationFacadeService extends FacadeService {
  constructor(public override store: Store<EnboxConfigurationState>) {
    super(store);
  }

  // OBSERVABLES

  nameEnbox$ = this.storeSelect(selectors.getNameEnbox);
  externalIdEnbox$ = this.storeSelect(selectors.getExternalIdEnbox);
  admissionFeeEnbox$ = this.storeSelect(selectors.getAdmissionFeeEnbox);
  creditCostEnbox$ = this.storeSelect(selectors.getCreditCostEnbox);
  segmentsRetentionDaysEnbox$ = this.storeSelect(selectors.getSegmentsRetentionDaysEnbox);
  baseUrlEnbox$ = this.storeSelect(selectors.getBaseUrlEnbox);
  connectionTimeoutEnbox$ = this.storeSelect(selectors.getConnectionTimeoutEnbox);
  readTimeoutEnbox$ = this.storeSelect(selectors.getReadTimeoutEnbox);
  apiKeyEnbox$ = this.storeSelect(selectors.getApiKeyEnbox);
  emailNotifier$ = this.storeSelect(selectors.getEmailNotifier);
  lookBackDays$ = this.storeSelect(selectors.getLookBackDays);
  selectedLanguageEnbox$ = this.storeSelect(selectors.getLanguageEnbox);
  selectedLanguageNamesEnbox$ = this.storeSelect(selectors.getLanguageNamesEnbox);
  selectedLanguageCodesEnbox$ = this.storeSelect(selectors.getLanguageCodesEnbox);
  isActiveEnbox$ = this.storeSelect(selectors.getStatusEnbox);
  languages$ = this.storeSelect(selectors.getLanguages);
  showUpdateBtn$ = this.storeSelect(selectors.getShowUpdateBtn);
  disableNameValue$ = this.storeSelect(selectors.getDisableNameValue);
  rootConfigurationHelpTextStatus$ = this.storeSelect(selectors.getRootConfigurationHelpTextStatus);
  credentialsConfigurationHelpTextStatus$ = this.storeSelect(selectors.getCredentialsConfigurationHelpTextStatus);
  campaignReportingHelpTextStatus$ = this.storeSelect(selectors.getCampaignReportingHelpTextStatus);
  endpointConfigurationHelpTextStatus$ = this.storeSelect(selectors.getEndpointConfigurationHelpTextStatus);
  selectedEnboxDataFromGrid$ = this.storeSelect(selectors.getSelectedEnboxDataFromGrid);
  showCancelBtn$ = this.storeSelect(selectors.getShowCancelBtn);
  enboxDataAreValid$ = this.storeSelect(selectors.getEnboxDataAreValid);
  showNameErrorMsg$ = this.storeSelect(selectors.getShowNameErrorMsg);
  showBaseUrlErrorMsg$ = this.storeSelect(selectors.getShowBaseUrlErrorMsg);
  showApiKeyErrorMsg$ = this.storeSelect(selectors.getShowApiKeyErrorMsg);
  emailNotifierErrorMsg$ = this.storeSelect(selectors.getEmailNotifierErrorMsg);
  lookBackDaysErrorMsg$ = this.storeSelect(selectors.getLookBackDaysErrorMsg);

  // OBSERVABLE'S VALUES

  get nameEnbox() {
    return this.storeSnapshot(this.nameEnbox$);
  }

  get externalIdEnbox() {
    return this.storeSnapshot(this.externalIdEnbox$);
  }

  get admissionFeeEnbox() {
    return this.storeSnapshot(this.admissionFeeEnbox$);
  }

  get creditCostEnbox() {
    return this.storeSnapshot(this.creditCostEnbox$);
  }

  get segmentsRetentionDaysEnbox() {
    return this.storeSnapshot(this.segmentsRetentionDaysEnbox$);
  }

  get baseUrlEnbox() {
    return this.storeSnapshot(this.baseUrlEnbox$);
  }

  get connectionTimeOutEnbox() {
    return this.storeSnapshot(this.connectionTimeoutEnbox$);
  }

  get readTimeOutEnbox() {
    return this.storeSnapshot(this.readTimeoutEnbox$);
  }

  get apiKeyEnbox() {
    return this.storeSnapshot(this.apiKeyEnbox$);
  }

  get emailNotifier() {
    return this.storeSnapshot(this.emailNotifier$);
  }

  get lookBackDays() {
    return this.storeSnapshot(this.lookBackDays$);
  }

  get selectedLanguageEnbox() {
    return this.storeSnapshot(this.selectedLanguageEnbox$);
  }

  get selectedLanguageCodesEnbox() {
    return this.storeSnapshot(this.selectedLanguageCodesEnbox$);
  }

  get statusEnbox() {
    return this.storeSnapshot(this.isActiveEnbox$);
  }

  get languages() {
    return this.storeSnapshot(this.languages$);
  }

  get showUpdateBtn() {
    return this.storeSnapshot(this.showUpdateBtn$);
  }

  get selectedEnboxDataFromGrid() {
    return this.storeSnapshot(this.selectedEnboxDataFromGrid$);
  }

  get enboxDataAreValid() {
    return this.storeSnapshot(this.enboxDataAreValid$);
  }

  getEnboxBody() {
    const integrationCode = this.nameEnbox;
    const creditCost = this.creditCostEnbox;
    const admitionFee = this.admissionFeeEnbox;
    const segmentRetentionDays = this.segmentsRetentionDaysEnbox;
    const externalListID = this.externalIdEnbox;
    const supportedSalutionLanguages = this.selectedLanguageCodesEnbox;
    const baseURL = this.baseUrlEnbox;
    const readTimeOut = this.readTimeOutEnbox;
    const connectionTimeOut = this.connectionTimeOutEnbox;
    const apiKey = this.apiKeyEnbox;
    const status = this.statusEnbox;
    const lookBackDays = this.lookBackDays;
    const emailNotifier = this.emailNotifier;

    const enboxBody = {
      integrationCode,
      deleted: status,
      integrationConfig: {
        integrationSystem: 'ENBOX',
        integrationDisplayName: integrationCode,
        creditCost,
        admitionFee,
        segmentRetentionDays,
        executionNotificationReceiver: emailNotifier,
        daysLookBack: lookBackDays,
        externalListID,
        supportedSalutionLanguages,
        abTestSizePercentage: null,
        abHoursToTest: null,
      },
      endpointConfiguration: {
        code: integrationCode,
        endpointConfig: {
          type: 'HTTP',
          method: 'POST',
          baseURL,
          headers: {
            'Content-Type': 'application/json',
          },
          skipSSL: false,
          pathParams: {},
          queryParams: {},
          readTimeOut,
          connectionTimeOut,
          dateFormat: null,
          requestContentType: 'application/json',
          responseContentType: 'application/json',
        },
        credentialConfig: {
          key: apiKey,
          type: 'API_KEY',
          bypassEncryption: false,
        },
      },
    };

    return enboxBody;
  }
}
